<template>
  <div v-if="data.length > itemsPerPage" class="mt-16 flex justify-start">
    <button
      type="button"
      class="mr-4 size-[1.375em] transition-colors"
      :disabled="currentIndex <= 1"
      :aria-label="$t('aria_labels.go_to_previous_page')"
      @click="paginate(currentIndex - 1)"
    >
      <IconArrowDown
        class="rotate-90 transition-opacity"
        :class="{ 'opacity-20': currentIndex <= 1 }"
      />
    </button>

    <div class="relative">
      <!-- Floating Background -->
      <div
        ref="floatingBackground"
        class="
          absolute -bottom-1 mt-auto h-0.5 rounded-full bg-blue-500 transition-[left,width,bottom]
          duration-300
        "
        :class="currentIndex >= 10 ? 'w-[1.25em]' : 'w-[1em]'"
        :style="{ left: `${backgroundLeft}px`, bottom: `${backgroundBottom}px` }"
      />

      <!-- Buttons -->
      <ul class="relative flex flex-wrap">
        <li v-for="(page, index) in pages" :key="index" class="flex w-fit justify-center px-4">
          <button
            :id="`pagination-button-${index}`"
            type="button"
            :aria-label="`${$t('aria_labels.go_to_page')} ${index + 1}`"
            class=""
            @click="paginate(index)"
          >
            <span :class="{ 'text-blue-500': currentIndex === index }">
              {{ index + 1 }}
            </span>
          </button>
        </li>
      </ul>
    </div>
    <button
      type="button"
      class="ml-4 size-[1.375em] transition-colors"
      :disabled="currentIndex >= pages.length - 1"
      :aria-label="$t('aria_labels.go_to_next_page')"
      @click="paginate(currentIndex + 1)"
    >
      <IconArrowDown
        class="-rotate-90 transition-opacity"
        :class="{ 'opacity-20': currentIndex >= pages.length - 1 }"
      />
    </button>
  </div>
</template>

<script setup lang="ts">
const props = defineProps({
  data: {
    type: Array,
    required: true,
  },
  itemsPerPage: {
    type: Number,
    default: 16,
  },
  scrollToId: {
    type: String,
    default: '',
  },
  shouldScroll: {
    type: Boolean,
    default: false,
  },
})

const route = useRoute()
const params = useUrlSearchParams('history')
const floatingBackground = ref<HTMLElement | null>(null)
const backgroundLeft = ref<number>(0)
const backgroundBottom = ref<number>(0)

// Get the initial index from the query
let initialIndex = parseInt(route.query[props.scrollToId]) || 0

// Clamp the initial index to the number of pages
initialIndex = Math.max(
  0,
  Math.min(
    initialIndex,
    Math.max(0, Math.ceil(props.data.length / props.itemsPerPage) - 1),
  ),
)
const currentIndex = ref(initialIndex)

const emit = defineEmits(['update'])

// Chunk the data into pages
const pages = computed(() => {
  if (!Array.isArray(props.data) || props.data.length === 0) {
    return []
  }

  const chunks = []
  for (let i = 0; i < props.data.length; i += props.itemsPerPage) {
    chunks.push(props.data.slice(i, i + props.itemsPerPage))
  }
  return chunks
})

paginate(currentIndex.value)

watch(
  () => props.data,
  async () => {
    await nextTick()
    paginate(0)
  },
)

function paginate(index) {
  if (index < 0 || index >= pages.value.length) {
    emit('update', [])
    return
  }

  updateFloatingBackground(index)

  if (props.shouldScroll && props.scrollToId && import.meta.client) {
    scrollToElement(props.scrollToId)
  }

  currentIndex.value = index
  emit('update', pages.value[index])

  // Set the query to the URL
  params[props.scrollToId] = currentIndex.value
}

async function updateFloatingBackground(index: number) {
  await nextTick()

  const activeButton = document.getElementById(`pagination-button-${index}`)
  if (activeButton && floatingBackground.value) {
    const buttonRect = activeButton.getBoundingClientRect()
    const parentRect = floatingBackground.value.parentElement?.getBoundingClientRect()
    if (parentRect) {
      const leftPosition = buttonRect.left - parentRect.left - 4
      backgroundLeft.value = leftPosition
      const bottomPosition = parentRect.bottom - buttonRect.bottom - 2
      backgroundBottom.value = bottomPosition
    }
  }
}

function scrollToElement(elementId) {
  const element = document.getElementById(elementId)

  if (!element) {
    return
  }

  const headerOffset = 250
  const elementPosition = element.offsetTop
  const offsetPosition = elementPosition - headerOffset

  window.scrollTo({
    top: offsetPosition,
    behavior: 'smooth',
  })
}

onMounted(() => {
  // Initialize the background position for the first page
  updateFloatingBackground(0)
})
</script>
